class RingmakerCustomizer {
  constructor() {
    this.state = this.defaultState();
    this._ringsizeTouched = false;

    this.artworksLoaded = false;
    this.cdnPrefix = window.RM_CDN_PREFIX || 'ring_thumb_';
    this.globalFilesUrl = window.RM_GLOBAL_FILES_URL || '';

    this.loadState();
    this.initStateFromURL();
    this.initElements();
    this.sanitizeState();
    this.bindEvents();

    // Initialize logger if the script is loaded
    if (typeof RingmakerLogger !== 'undefined') {
      this.logger = new RingmakerLogger(this);
    }

    this.renderState();
    this.initA11yState();
  }

  // §10S item 1 (2026-09-29): screen readers hear which option is chosen. aria-pressed mirrors the .active class of every
  // option and ring-size button, whichever code path toggles it (a class observer instead of edits at each toggle).
  initA11yState() {
    if (!this.container || typeof MutationObserver === 'undefined') return;
    const SEL = '.rm-option-btn, .rm-size-option';
    const sync = (b) => { const on = String(b.classList.contains('active')); if (b.getAttribute('aria-pressed') !== on) b.setAttribute('aria-pressed', on); };
    this.container.querySelectorAll(SEL).forEach(sync);
    new MutationObserver((records) => {
      records.forEach((r) => {
        if (r.type === 'attributes') { if (r.target.matches && r.target.matches(SEL)) sync(r.target); return; }
        r.addedNodes.forEach((n) => { if (n.nodeType !== 1) return; if (n.matches(SEL)) sync(n); n.querySelectorAll(SEL).forEach(sync); });
      });
    }).observe(this.container, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
  }

  defaultState() {
    return {
      color: 'Silver',
      design: 'Raised',
      shape: 'Oval',
      size: 'Medium',
      ringsize: 'Send me a free ring sizer',
      upload_file: null,
      side_engravings: 'Plain',
      left_shoulder_type: '',
      left_shoulder_value: '',
      left_shoulder_file: null,
      right_shoulder_type: '',
      right_shoulder_value: '',
      right_shoulder_file: null,
      inscription_type: 'Plain',
      inscription: ''
    };
  }

  loadState() {
    try {
      const keys = ['royal_saved_designs', 'rm_saved_design_v1', 'ringmaker_state'];
      for (const key of keys) {
        const raw = window.localStorage && window.localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const entry = Array.isArray(parsed) ? parsed[parsed.length - 1] : parsed;
        if (entry && typeof entry === 'object') {
          const savedSize = entry.ringsize || entry.ring_size || entry.ringSize;
          if (savedSize && typeof savedSize === 'string') {
            this.state.ringsize = this.cleanParam(savedSize, false);
            this._ringsizeTouched = true;
            break;
          }
        }
      }
    } catch (e) {}
  }

  // Shared links (2026-09-29, CUSTOM_THEME §10U round 3): a link can arrive mangled by the app it travelled through
  // (the logs hold "I&#39;ll let you know later", "Medium)â", "US+12") or carry a value the designer does not offer.
  // cleanParam() undoes the usual damage; sanitizeState() keeps a value only when it is a real choice (else the longest
  // choice it starts with, so trailing junk drops off) and otherwise falls back to the default.
  cleanParam(value, isText) {
    const v = String(value == null ? '' : value).replace(/&#0*39;|&apos;/gi, "'").replace(/&quot;/gi, '"').replace(/&amp;/gi, '&');
    return isText ? v : v.replace(/\+/g, ' ').trim();
  }

  sanitizeState() {
    if (!this.container) return;
    const defaults = this.defaultState();
    const pick = (value, choices) => {
      if (!value || choices.includes(value)) return value;
      return choices.filter(c => value.startsWith(c)).sort((a, b) => b.length - a.length)[0] || '';
    };
    const choicesOf = (option) => Array.from(this.container.querySelectorAll(`.rm-option-btn[data-option="${option}"]`)).map(b => b.dataset.value);
    ['color', 'design', 'shape', 'size', 'side_engravings', 'inscription_type'].forEach(option => {
      const choices = choicesOf(option);
      if (choices.length) this.state[option] = pick(this.state[option], choices) || defaults[option];
    });
    ['left_shoulder', 'right_shoulder'].forEach(side => {
      const choices = choicesOf(side);
      if (choices.length) this.state[side + '_type'] = pick(this.state[side + '_type'], choices);
    });
    const ringSizes = Array.from(this.container.querySelectorAll('.rm-size-option')).map(b => b.dataset.value);
    if (ringSizes.length) this.state.ringsize = pick(this.state.ringsize, ringSizes) || defaults.ringsize;
  }

  // Engraving texts that reach the order (Mike 2026-09-29, "7 = Yes"): the inside inscription only while "Inscribed" is
  // chosen (text typed before switching back to "Plain" stays out); spaces around a text are trimmed and a text of
  // spaces alone counts as empty.
  inscriptionText() {
    return this.state.inscription_type === 'Inscribed' ? String(this.state.inscription || '').trim() : '';
  }

  shoulderValid(side) {
    const type = this.state[side + '_type'];
    if (type === 'Text' || type === 'Artwork') return !!String(this.state[side + '_value'] || '').trim();
    if (type === 'Upload') return !!(this.state[side + '_file'] && this.state[side + '_file'].length > 0);
    return false;
  }

  initStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    if (!params.toString()) return;

    const mapping = {
      color: 'color', design: 'design', shape: 'shape', size: 'size', ringsize: 'ringsize',
      side_engravings: 'side_engravings', left_shoulder_type: 'left_shoulder_type',
      left_shoulder_value: 'left_shoulder_value', right_shoulder_type: 'right_shoulder_type',
      right_shoulder_value: 'right_shoulder_value', inscription_type: 'inscription_type',
      inscription: 'inscription'
    };

    const textKeys = ['inscription', 'left_shoulder_value', 'right_shoulder_value'];
    for (const [stateKey, urlKey] of Object.entries(mapping)) {
      if (params.has(urlKey)) {
        this.state[stateKey] = this.cleanParam(params.get(urlKey), textKeys.includes(stateKey));
      }
    }
    if (params.has('ringsize') && this.state.ringsize) {
      this._ringsizeTouched = true;
    } else if (!params.has('ringsize') && params.has('size')) {
      const rawSize = this.cleanParam(params.get('size'), false);
      if (/^(US\s*\d|Send me a free ring sizer|I'll let you know later)/i.test(rawSize)) {
        this.state.ringsize = rawSize;
        this.state.size = this.defaultState().size;
        this._ringsizeTouched = true;
      }
    }

    const fileKeys = ['upload_file', 'left_shoulder_file', 'right_shoulder_file'];
    const urlFileKeys = ['upload', 'left_file', 'right_file'];

    urlFileKeys.forEach((urlKey, index) => {
      if (params.has(urlKey)) {
        const filenames = params.get(urlKey).split(',');
        this.state[fileKeys[index]] = filenames.map(name => {
          if (name.includes('cdn.shopify.com') || name.includes('/uploads/')) return name;
          return this.globalFilesUrl + name;
        });
      }
    });
  }

  syncStateToURL() {
    const currentParams = new URLSearchParams(window.location.search);
    const params = new URLSearchParams();
    if (currentParams.has('view')) {
      params.set('view', currentParams.get('view'));
    }

    const mapping = {
      color: 'color', design: 'design', shape: 'shape', size: 'size', ringsize: 'ringsize',
      side_engravings: 'side_engravings', left_shoulder_type: 'left_shoulder_type',
      left_shoulder_value: 'left_shoulder_value', right_shoulder_type: 'right_shoulder_type',
      right_shoulder_value: 'right_shoulder_value', inscription_type: 'inscription_type',
      inscription: 'inscription'
    };

    for (const [stateKey, urlKey] of Object.entries(mapping)) {
      if (this.state[stateKey]) {
        // Skip side_engravings fields if side_engravings is Plain
        if (this.state.side_engravings === 'Plain' && ['side_engravings', 'left_shoulder_type', 'left_shoulder_value', 'right_shoulder_type', 'right_shoulder_value'].includes(stateKey)) {
          continue;
        }
        // Skip inscription fields if inscription_type is Plain
        if (this.state.inscription_type === 'Plain' && ['inscription_type', 'inscription'].includes(stateKey)) {
          continue;
        }
        params.set(urlKey, this.state[stateKey]);
      }
    }

    const fileKeys = ['upload_file', 'left_shoulder_file', 'right_shoulder_file'];
    const urlFileKeys = ['upload', 'left_file', 'right_file'];

    fileKeys.forEach((stateKey, index) => {
      // Skip side engravings files if plain
      if (this.state.side_engravings === 'Plain' && (stateKey === 'left_shoulder_file' || stateKey === 'right_shoulder_file')) {
        return;
      }

      const files = this.state[stateKey];
      if (files && files.length > 0) {
        // If it's a native File, get its name. If it's a string URL from init, extract filename
        const names = files.map(file => {
          if (typeof file === 'string') {
            if (file.includes('cdn.shopify.com') || file.includes('/uploads/')) return file;
            return file.split('/').pop().split('?')[0];
          }
          return file.name;
        });
        params.set(urlFileKeys[index], names.join(','));
      }
    });

    const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + params.toString();
    window.history.replaceState({ path: newUrl }, '', newUrl);
  }

  updateSummaryIsland() {
    const textEl = document.getElementById('rm-summary-text');
    if (!textEl) return;

    const parts = [
      this.state.color,
      this.state.design,
      this.state.shape,
      this.state.size
    ];
    if (this.state.ringsize) {
      if (this.state.ringsize === 'Send me a free ring sizer') {
        parts.push('Free ring sizer');
      } else if (this.state.ringsize === "I'll let you know later") {
        parts.push('Confirm size later');
      } else if (this.state.ringsize === 'Deferred') {
        parts.push('Size ?');
      } else {
        parts.push(this.state.ringsize);
      }
    }

    textEl.textContent = parts.join(' • ');
  }

  initElements() {
    this.container = document.getElementById('ringmaker-customizer');
    if (!this.container) return;

    this.buttons = this.container.querySelectorAll('.rm-option-btn');
    this.deferButtonsText = this.container.querySelectorAll('.rm-defer-btn-text');

    // File upload
    this.fileInput = document.getElementById('rm-artwork-input');
    this.uploadPreview = document.getElementById('rm-upload-preview');
    this.previewImage = document.getElementById('rm-preview-image');
    this.removeUploadBtn = document.getElementById('rm-remove-upload');

    // Upload Modal
    this.uploadHelpBtn = document.getElementById('rm-upload-help-btn');
    this.uploadModalOverlay = document.getElementById('rm-upload-modal-overlay');
    if (this.uploadModalOverlay) document.body.appendChild(this.uploadModalOverlay);
    this.uploadModalClose = document.getElementById('rm-upload-modal-close');

    // Size Modal
    this.sizeHelpBtn = document.getElementById('rm-size-help-btn');
    this.sizeModalOverlay = document.getElementById('rm-size-modal-overlay');
    if (this.sizeModalOverlay) document.body.appendChild(this.sizeModalOverlay);
    this.sizeModalClose = document.getElementById('rm-size-modal-close');
    this.sizeModalCloseBtn = document.getElementById('rm-size-modal-close-btn');

    // Ring size
    this.allSizeOptions = this.container.querySelectorAll('.rm-size-option');
    this.needHelpBtn = document.getElementById('rm-need-help-btn');
    this.helpInfoBoxes = document.getElementById('rm-help-info-boxes');
    this.infoBoxSizer = document.getElementById('rm-info-sizer');
    this.infoBoxLater = document.getElementById('rm-info-later');
    this.infoBoxSize = document.getElementById('rm-info-size');
    this.carouselPrev = document.getElementById('rm-carousel-prev');
    this.carouselNext = document.getElementById('rm-carousel-next');
    this.carouselItems = document.getElementById('rm-carousel-items');

    // Advanced options
    this.advancedToggle = document.getElementById('rm-advanced-toggle');
    this.advancedContainer = document.getElementById('rm-advanced-container');
    this.advancedOverlay = document.getElementById('rm-advanced-overlay');
    this.inscriptionInput = document.getElementById('rm-inscription-input');
    this.inscriptionDefer = document.getElementById('rm-inscription-defer');

    // Checkout
    this.addToCartBtn = document.getElementById('rm-add-to-cart');
    this.shopPayBtn = this.container.querySelector('.rm-shop-pay-btn');
    this.priceDisplay = document.getElementById('rm-total-price');

    // Summary Island Actions
    this.saveBuildBtn = document.getElementById('rm-save-build-btn');
    this.shareBuildBtn = document.getElementById('rm-share-build-btn');
    this.resetBuildBtn = document.getElementById('rm-reset-build-btn');

    // Initialize ring size picker
    if (this.carouselItems) {
      this.isProgrammaticScroll = true;
      const initScroll = () => {
        if (this.carouselItems.offsetWidth === 0) {
          setTimeout(initScroll, 100);
          return;
        }
        if (this.state.ringsize && this.state.ringsize.startsWith('US')) {
          const btn = Array.from(this.allSizeOptions).find(opt => opt.dataset.value === this.state.ringsize);
          if (btn) {
            requestAnimationFrame(() => {
              const scrollTarget = btn.offsetLeft - (this.carouselItems.offsetWidth / 2) + (btn.offsetWidth / 2);
              this.carouselItems.scrollTo({ left: scrollTarget, behavior: 'auto' });
            });
            setTimeout(() => { this.isProgrammaticScroll = false; }, 300);
            return;
          }
        }
        
        // Fallback to default US 10
        const defaultBtn = Array.from(this.carouselItems.querySelectorAll('.rm-carousel-size-btn'))[12];
        if (defaultBtn) {
          requestAnimationFrame(() => {
            const scrollTarget = defaultBtn.offsetLeft - (this.carouselItems.offsetWidth / 2) + (defaultBtn.offsetWidth / 2);
            this.carouselItems.scrollTo({ left: scrollTarget, behavior: 'auto' });
          });
        }
        setTimeout(() => { this.isProgrammaticScroll = false; }, 300);
      };
      setTimeout(initScroll, 100);
    }

    // CUSTOM_THEME §10W W1/W2 (Mike 2026-10-02): the other colour's pictures wait for the load event and an idle moment, so
    // they no longer share the first screen with the gallery photo and the visible options (steel: 28 files, 574 KB at 1.6 s).
    // A colour tap before then loads its own pictures as before. Skipped when the visitor asked to save data.
    const preload = () => this.preloadSecondaryAssets();
    const whenIdle = () => (window.requestIdleCallback ? window.requestIdleCallback(preload, { timeout: 3000 }) : setTimeout(preload, 1000));
    if (!(navigator.connection && navigator.connection.saveData)) {
      if (document.readyState === 'complete') whenIdle(); else window.addEventListener('load', whenIdle, { once: true });
    }
  }

  preloadSecondaryAssets() {
    const keys = ['gold', 'rosegold'];
    const designs = ['raised', 'raised_black', 'recessed', 'recessed_black', 'waxseal']; // black-background buttons too (§10W, 2026-10-02 live round)
    const shapes = ['oval', 'cushion', 'round'];
    const sizes = ['small', 'medium', 'large'];
    const sides = ['plain', 'engraved'];
    const sideSubs = ['text', 'artwork', 'upload'];

    keys.forEach(color => {
      designs.forEach(d => { const img = new Image(); img.src = this.getAssetUrl(`design_${color}_${d}.webp`); });
      shapes.forEach(s => { const img = new Image(); img.src = this.getAssetUrl(`shape_${color}_${s}.webp`); });
      sizes.forEach(sz => { const img = new Image(); img.src = this.getAssetUrl(`size_${color}_${sz}.webp`); });
      sides.forEach(sd => { const img = new Image(); img.src = this.getAssetUrl(`sides_${color}_${sd}.webp`); });
      sideSubs.forEach(sub => { const img = new Image(); img.src = this.getAssetUrl(`sides_${color}_engraved_${sub}.webp`); });
    });
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Customer uploads (CUSTOM_THEME §10R, Mike's items #17 and #18, approved 2026-09-28).
  // - prepareUpload(): a photo over 16 MP or 15 MB that the browser can decode is redrawn at <= 4,096 px on the long
  //   edge (JPEG 0.9, PNG when it has transparency) before it is sent; anything else is sent as it is and the server
  //   normalises it. Shopify refuses images over 25 MP, so this is what lets those uploads through.
  // - uploadToCDN(): retries only failures that can succeed on a retry (network, timeout, HTTP 5xx); a permanent answer
  //   from the server (reason unsupported_type / too_large, or the old server's "processing failed") stops at once and
  //   becomes a calm message with a way forward.
  // - A file that did not upload never enters the state, so no '[Pending Upload]' placeholder can reach an order
  //   (R6630), and Add to Cart waits for uploads that are still running instead of flagging the step as missing.
  // ---------------------------------------------------------------------------------------------------------------
  loadUploadImage(file) {
    // Resolves { source, width, height, release } for a picture this browser can decode (EXIF orientation applied),
    // or null when it cannot (for example HEIC or DNG outside Safari).
    const viaImg = () => new Promise(resolve => {
      let url = null;
      try { url = URL.createObjectURL(file); } catch (e) { resolve(null); return; }
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        if (!img.naturalWidth || !img.naturalHeight) { URL.revokeObjectURL(url); resolve(null); return; }
        resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) });
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
    if (typeof window.createImageBitmap === 'function') {
      return window.createImageBitmap(file, { imageOrientation: 'from-image' })
        .then(bmp => ({ source: bmp, width: bmp.width, height: bmp.height, release: () => { try { bmp.close(); } catch (e) {} } }))
        .catch(() => viaImg());
    }
    return viaImg();
  }

  uploadHasTransparency(source, width, height, type, name) {
    // JPEG cannot carry transparency; for other formats look at a small copy of the picture. When unsure, say yes:
    // a logo must never be flattened onto a background by mistake.
    if (/jpe?g/.test(type) || /\.jpe?g$/i.test(name || '')) return false;
    try {
      const s = Math.min(1, 256 / Math.max(width, height));
      const w = Math.max(1, Math.round(width * s));
      const h = Math.max(1, Math.round(height * s));
      const probe = document.createElement('canvas');
      probe.width = w;
      probe.height = h;
      const ctx = probe.getContext('2d', { willReadFrequently: true });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(source, 0, 0, w, h);
      const px = ctx.getImageData(0, 0, w, h).data;
      for (let i = 3; i < px.length; i += 4) {
        if (px[i] < 255) return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  async prepareUpload(file) {
    const MAX_EDGE = 4096;
    const MAX_PIXELS = 16000000;
    const MAX_BYTES = 15 * 1024 * 1024;
    const type = String((file && file.type) || '').toLowerCase();
    // Vector files keep their quality as they are; the server handles them.
    if (!file || type === 'image/svg+xml' || /\.svg$/i.test(file.name || '')) return file;
    let decoded = null;
    try {
      decoded = await Promise.race([this.loadUploadImage(file), new Promise(r => setTimeout(() => r(null), 20000))]);
      if (!decoded) return file;
      const { width, height } = decoded;
      if (width * height <= MAX_PIXELS && file.size <= MAX_BYTES) return file;
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const keepAlpha = this.uploadHasTransparency(decoded.source, width, height, type, file.name);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      if (!keepAlpha) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
      }
      ctx.drawImage(decoded.source, 0, 0, w, h);
      const outType = keepAlpha ? 'image/png' : 'image/jpeg';
      const blob = await new Promise(resolve => {
        try { canvas.toBlob(resolve, outType, 0.9); } catch (e) { resolve(null); }
      });
      canvas.width = 0; // hand the canvas memory back at once (iOS keeps it otherwise)
      canvas.height = 0;
      if (!blob || !blob.size || blob.type !== outType) return file;
      if (scale === 1 && blob.size >= file.size) return file; // re-encoding alone did not make it smaller
      const base = String(file.name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
      return new File([blob], `${base}.${keepAlpha ? 'png' : 'jpg'}`, { type: outType, lastModified: Date.now() });
    } catch (e) {
      console.warn('[Upload] could not resize in the browser, sending the original', e);
      return file;
    } finally {
      if (decoded && decoded.release) decoded.release();
    }
  }

  uploadFailure(status, data, cause) {
    // New server: JSON with reason unsupported_type | too_large | shopify_failed | timeout (top level or inside detail).
    // Old server: HTTP 500 {"detail": "<text>"} for every failure.
    const detail = data ? data.detail : null;
    const reason = (data && (data.reason || (detail && typeof detail === 'object' && detail.reason))) || '';
    const text = typeof detail === 'string' ? detail : ((detail && detail.message) || (data && data.message) || (cause && cause.message) || '');
    const err = new Error(text || (status ? `Upload failed (HTTP ${status})` : 'Upload failed (network)'));
    err.rmUpload = true;
    err.status = status;
    if (reason) {
      err.reason = reason;
      err.permanent = reason === 'unsupported_type' || reason === 'too_large' || (reason === 'shopify_failed' && status > 0 && status < 500);
    } else if (!status) {
      err.reason = cause && cause.name === 'AbortError' ? 'timeout' : 'network';
      err.permanent = false;
    } else if (status === 413 || status === 415) {
      err.reason = status === 413 ? 'too_large' : 'unsupported_type';
      err.permanent = true;
    } else if (status >= 500 || status === 408 || status === 429) {
      // The old server passes on Shopify's own verdict on the file; the same bytes get the same verdict again.
      err.permanent = /processing failed|fileCreate failed|INVALID_IMAGE|maximum resolution|pixel limit/i.test(text);
      err.reason = err.permanent ? 'unsupported_type' : 'server';
    } else {
      err.reason = 'rejected';
      err.permanent = true;
    }
    return err;
  }

  async uploadToCDN(file, shopName) {
    // Uploads to Shopify Files via the App Proxy (Render backend). Resolves with a permanent CDN URL string or throws
    // the classified error of the last attempt. Never returns a '[Pending Upload]' placeholder (Order R6630 incident).
    const maxAttempts = 3;
    const backoffs = [1000, 2500];
    // A slow phone connection needs longer than a fixed minute for a big photo: 60 s plus 10 s per MB, at most 3 min.
    const timeoutMs = Math.min(180000, 60000 + Math.ceil((file.size || 0) / 1000000) * 10000);
    let sessionId = '';
    try { sessionId = sessionStorage.getItem('rm_session_id') || ''; } catch (e) {}
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('rm_session_id', sessionId);
        const response = await fetch(`/apps/ringmaker-logs/upload?shop=${encodeURIComponent(shopName)}`, {
          method: 'POST',
          body: formData,
          signal: controller ? controller.signal : undefined
        });
        let data = null;
        try { data = await response.json(); } catch (e) { data = null; }
        if (response.ok && data && data.url) {
          // Cache buster prevents the browser caching a 404 while the CDN propagates
          return data.url + (data.url.includes('?') ? '&' : '?') + 't=' + Date.now();
        }
        throw this.uploadFailure(response.status, data);
      } catch (err) {
        const failure = err && err.rmUpload ? err : this.uploadFailure(0, null, err);
        lastErr = failure;
        console.error(`[Upload Error] attempt ${attempt}/${maxAttempts} (${failure.reason})`, failure.message);
        if (failure.permanent || attempt === maxAttempts) break;
        this.state.upload_retries = (this.state.upload_retries || 0) + 1;
        await new Promise(r => setTimeout(r, backoffs[attempt - 1]));
      } finally {
        if (timer) clearTimeout(timer);
      }
    }
    throw lastErr || this.uploadFailure(0, null, new Error('Upload failed'));
  }

  uploadErrorMessage(err, fileName, several) {
    const subject = several && fileName ? `"${fileName}"` : 'This file';
    if (err && err.reason === 'too_large') {
      return `${subject} is too large to upload. Please choose a smaller copy or a screenshot of it, or e-mail it to support@royalsignets.com.`;
    }
    if (err && err.permanent && err.reason !== 'rejected') {
      return `${subject} couldn't be read. Please choose another photo or a screenshot of it, or e-mail it to support@royalsignets.com.`;
    }
    return `${subject} didn't upload. Please try again, choose another photo or a screenshot of it, or e-mail it to support@royalsignets.com.`;
  }

  showUploadError(dropzoneId, messages) {
    // Calm, helpful note under the upload box (not an error banner); the e-mail address is a mailto link.
    const dropzone = document.getElementById(dropzoneId);
    if (!dropzone) return;
    const list = (Array.isArray(messages) ? messages : [messages]).filter(Boolean).slice(0, 3);
    if (!list.length) { this.clearUploadError(dropzoneId); return; }
    let errEl = dropzone.querySelector('.rm-upload-error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'rm-upload-error';
      errEl.setAttribute('role', 'status');
      errEl.style.cssText = 'color:#374151;font-size:15px;line-height:1.45;font-weight:500;margin-top:10px;text-align:center;';
      dropzone.appendChild(errEl);
    }
    errEl.textContent = '';
    const address = 'support@royalsignets.com';
    list.forEach((message, i) => {
      const line = document.createElement('p');
      line.style.cssText = 'margin:' + (i ? '6px' : '0') + ' 0 0;';
      String(message).split(address).forEach((part, j, parts) => {
        line.appendChild(document.createTextNode(part));
        if (j < parts.length - 1) {
          const a = document.createElement('a');
          a.href = 'mailto:' + address + '?subject=' + encodeURIComponent('Artwork for my signet ring');
          a.textContent = address;
          a.style.cssText = 'color:inherit;text-decoration:underline;';
          line.appendChild(a);
        }
      });
      errEl.appendChild(line);
    });
  }

  clearUploadError(dropzoneId) {
    const dropzone = document.getElementById(dropzoneId);
    const errEl = dropzone ? dropzone.querySelector('.rm-upload-error') : null;
    if (errEl) errEl.remove();
  }

  trackPendingUpload(job) {
    if (!this.pendingUploads) this.pendingUploads = new Set();
    this.pendingUploads.add(job);
    const done = () => { this.pendingUploads.delete(job); };
    job.then(done, done);
    return job;
  }

  async waitForPendingUploads() {
    // Add to Cart during an upload waits for it to finish (at most 4 minutes) instead of marking the step missing.
    const jobs = this.pendingUploads ? Array.from(this.pendingUploads) : [];
    if (!jobs.length) return;
    await Promise.race([
      Promise.all(jobs.map(j => j.then(() => {}, () => {}))),
      new Promise(r => setTimeout(r, 240000))
    ]);
  }

  async ensureUploadsCompleted() {
    // Checkout safety net: any artwork still held as a local File object gets one final upload pass. Resolves with the
    // state key whose file could not be sent (the caller blocks the cart), or null when every artwork is a CDN URL.
    const shopName = window.Shopify ? window.Shopify.shop : null;
    const keys = ['upload_file', 'left_shoulder_file', 'right_shoulder_file'];
    for (const key of keys) {
      const arr = this.state[key];
      if (!arr || !arr.length) continue;
      for (let i = 0; i < arr.length; i++) {
        if (typeof arr[i] === 'string') continue;
        if (!shopName) return key; // no App Proxy here: never write a placeholder instead
        try {
          arr[i] = await this.uploadToCDN(await this.prepareUpload(arr[i]), shopName);
        } catch (err) {
          console.error('[Upload Error] final re-attempt before checkout failed', err);
          return key;
        }
      }
    }
    return null;
  }

  uploadZoneFor(stateKey) {
    return {
      upload_file: { dropzone: 'rm-upload-dropzone', step: 'upload' },
      left_shoulder_file: { dropzone: 'rm-left-upload-dropzone', step: 'left_shoulder' },
      right_shoulder_file: { dropzone: 'rm-right-upload-dropzone', step: 'right_shoulder' }
    }[stateKey] || null;
  }

  setupUploadZone(inputId, previewContainerId, dropzoneId, spinnerId, removeBtnId, stateKey) {
    const fileInput = document.getElementById(inputId);
    const removeBtn = document.getElementById(removeBtnId);
    // Bumped when the customer removes these files, switches the side-engraving type or resets the design: an upload
    // still running then is dropped instead of bringing the file back when it finishes (2026-09-29).
    if (!this.uploadEpoch) this.uploadEpoch = {};
    this.uploadEpoch[stateKey] = 0;

    const updatePreview = () => {
      const existingFiles = this.state[stateKey] || [];
      const previewContainer = document.getElementById(previewContainerId);
      if (previewContainer) previewContainer.innerHTML = '';

      const dataTransfer = new DataTransfer();
      existingFiles.forEach(file => {
        if (typeof file !== 'string') {
          dataTransfer.items.add(file);
        }
      });
      if (fileInput) fileInput.files = dataTransfer.files;

      if (existingFiles.length === 0) {
        const dropzone = document.getElementById(dropzoneId);
        if (dropzone) {
          dropzone.classList.remove('has-files');
          const previewBlock = dropzone.querySelector('.rm-upload-preview');
          if (previewBlock) previewBlock.classList.add('hidden');
        }
        return;
      }

      existingFiles.forEach((file, index) => {
        const itemDiv = document.createElement('div');
        itemDiv.className = 'rm-preview-item';

        const img = document.createElement('img');
        img.alt = `Uploaded Artwork ${index + 1}`;
        img.className = 'rm-preview-thumb';

        const removeSingleBtn = document.createElement('button');
        removeSingleBtn.className = 'rm-remove-single-btn';
        removeSingleBtn.setAttribute('aria-label', `Remove file ${index + 1}`);
        removeSingleBtn.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        removeSingleBtn.type = 'button';
        removeSingleBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.state[stateKey] = (this.state[stateKey] || []).filter(f => f !== file);
          updatePreview();
          this.renderState();
        });

        if (typeof file === 'string') {
          // Pre-uploaded from URL parameter or staged upload
          img.src = (file.startsWith('http') || file.startsWith('//')) ? file : this.getAssetUrl(file);
          itemDiv.appendChild(img);
          itemDiv.appendChild(removeSingleBtn);
          if (previewContainer) previewContainer.appendChild(itemDiv);
        } else {
          // Native file upload
          const reader = new FileReader();
          reader.onload = (ev) => {
            img.src = ev.target.result;
            itemDiv.appendChild(img);
            itemDiv.appendChild(removeSingleBtn);
            if (previewContainer) previewContainer.appendChild(itemDiv);
          };
          reader.readAsDataURL(file);
        }
      });

      const dropzone = document.getElementById(dropzoneId);
      if (dropzone) {
        dropzone.classList.add('has-files');
        const previewBlock = dropzone.querySelector('.rm-upload-preview');
        if (previewBlock) previewBlock.classList.remove('hidden');
      }
    };

    const handleSelection = async (files) => {
      const spinner = document.getElementById(spinnerId);
      if (spinner) spinner.classList.remove('hidden');

      const shopName = window.Shopify ? window.Shopify.shop : null;
      const added = [];
      const notes = [];
      const epoch = this.uploadEpoch[stateKey];

      this.clearUploadError(dropzoneId);
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          if (shopName) {
            try {
              const ready = await this.prepareUpload(file);
              const bustedUrl = await this.uploadToCDN(ready, shopName);
              added.push(bustedUrl);
            } catch (err) {
              // Do NOT fall back to a local File object: it can never reach the order and previously produced
              // '[Pending Upload]' placeholders in Shopify orders (R6630 incident). Tell the customer what to do instead.
              notes.push(this.uploadErrorMessage(err, file.name, files.length > 1));
            }
          } else {
            // Local dev fallback if shop is not available (no App Proxy on localhost); submitCart refuses to send it.
            const ext = file.name.split('.').pop();
            const newName = `CUSTOMER_UPLOAD_${Date.now()}_${i}.${ext}`;
            added.push(new File([file], newName, { type: file.type }));
          }
        }

        // Removed or reset while this upload ran: the customer no longer wants these files.
        if (this.uploadEpoch[stateKey] !== epoch) return;
        // Merge into the state as it is now (the customer may have removed other files while this upload ran).
        this.state[stateKey] = (this.state[stateKey] || []).concat(added);
        updatePreview();
        if (notes.length) this.showUploadError(dropzoneId, notes);
      } finally {
        if (spinner) spinner.classList.add('hidden');
      }
      this.renderState();

      const dropzone = document.getElementById(dropzoneId);
      const currentStep = dropzone ? dropzone.closest('.rm-step') : null;
      if (currentStep) {
        currentStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (fileInput) {
      fileInput.addEventListener('change', (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
          this.trackPendingUpload(handleSelection(files)).catch(err => console.error('[Upload Error] selection', err));
        }
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.uploadEpoch[stateKey]++;
        this.state[stateKey] = null;
        this.clearUploadError(dropzoneId);
        updatePreview();
        this.renderState();
      });
    }

    // Initialize preview if files exist in state from URL
    updatePreview();
  }

  wireDialog(overlay, openBtn, closeBtns, titleId) {
    if (!overlay) return;
    const closers = closeBtns.filter(Boolean);
    closers.forEach(btn => {
      if (!btn.getAttribute('aria-label') && !btn.textContent.trim()) btn.setAttribute('aria-label', 'Close');
      btn.querySelectorAll('svg').forEach(svg => svg.setAttribute('aria-hidden', 'true'));
    });
    if (!overlay.getAttribute('role')) overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    if (!overlay.getAttribute('aria-labelledby')) {
      const title = overlay.querySelector('.rm-modal-main-title');
      if (title) {
        if (!title.id) title.id = titleId;
        overlay.setAttribute('aria-labelledby', title.id);
      }
    }
    let returnTo = null;
    const open = () => {
      returnTo = document.activeElement;
      overlay.classList.remove('hidden');
      if (closers[0]) setTimeout(() => closers[0].focus({ preventScroll: true }), 0);
    };
    const close = () => {
      if (overlay.classList.contains('hidden')) return;
      overlay.classList.add('hidden');
      const back = returnTo && returnTo !== document.body ? returnTo : openBtn;
      returnTo = null;
      if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
    };
    if (openBtn) openBtn.addEventListener('click', open);
    closers.forEach(btn => btn.addEventListener('click', close));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    overlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const f = Array.from(overlay.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')).filter(el => el.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !overlay.classList.contains('hidden')) { e.preventDefault(); close(); }
    });
  }

  // Save and Share copy the design link. In-app browsers (Facebook, Instagram: most ring-designer visits) often refuse
  // the clipboard; then the older copy command is tried, and when that fails too the link is shown, selected, in the
  // note above the buttons so it can be copied by hand (2026-09-29, §10U round 3: the tap used to do nothing).
  copyDesignLink(thoughtId, fallbackText) {
    const url = window.location.href;
    const legacyCopy = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, url.length);
        const ok = document.execCommand('copy');
        ta.remove();
        return ok;
      } catch (e) {
        return false;
      }
    };
    const showNote = (copied) => {
      const box = document.getElementById(thoughtId);
      if (!box) return copied;
      if (box.dataset.rmText === undefined) box.dataset.rmText = box.textContent.trim();
      box.textContent = copied ? box.dataset.rmText : fallbackText;
      if (!copied) {
        const field = document.createElement('input');
        field.type = 'text';
        field.readOnly = true;
        field.value = url;
        field.setAttribute('aria-label', 'Link to your ring design');
        field.style.cssText = 'display:block;width:100%;margin-top:8px;padding:8px;font-size:14px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#111;';
        box.appendChild(field);
        setTimeout(() => { field.focus({ preventScroll: true }); field.select(); }, 0);
      }
      box.classList.remove('hidden');
      return copied;
    };
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard.writeText(url).then(() => showNote(true), () => showNote(legacyCopy()));
    }
    return Promise.resolve(showNote(legacyCopy()));
  }

  // Add to Cart that fails now says so under the button (the error box the code wrote to never existed in the page, so
  // a failed add used to end silently); the wording is calm and offers a way forward, the technical reason goes to the
  // log only. Missing-step messages are read out to screen-reader users through a hidden live region.
  showCartError(show) {
    let box = document.getElementById('rm-validation-error');
    if (!show) {
      if (box) box.classList.add('hidden');
      return;
    }
    if (!box && this.addToCartBtn) {
      box = document.createElement('div');
      box.id = 'rm-validation-error';
      box.setAttribute('role', 'alert');
      box.style.cssText = 'color:#374151;font-size:15px;line-height:1.45;font-weight:500;margin-top:10px;text-align:center;';
      this.addToCartBtn.insertAdjacentElement('afterend', box);
    }
    if (!box) return;
    const address = 'support@royalsignets.com';
    box.textContent = "Sorry, your ring couldn't be added to the cart just now. Please tap Add to Cart again. If it keeps happening, e-mail ";
    const a = document.createElement('a');
    a.href = 'mailto:' + address + '?subject=' + encodeURIComponent('My signet ring order');
    a.textContent = address;
    a.style.cssText = 'color:inherit;text-decoration:underline;';
    box.appendChild(a);
    box.appendChild(document.createTextNode(" and we'll finish your order with you."));
    box.classList.remove('hidden');
  }

  announce(message) {
    if (!this.container) return;
    let live = document.getElementById('rm-sr-status');
    if (!live) {
      live = document.createElement('div');
      live.id = 'rm-sr-status';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
      this.container.appendChild(live);
    }
    live.textContent = '';
    setTimeout(() => { live.textContent = message; }, 60);
  }

  setAdvancedExpanded(expanded) {
    if (!this.advancedContainer) return;
    this.advancedContainer.classList.toggle('expanded', expanded);
    if (this.advancedOverlay) this.advancedOverlay.style.opacity = expanded ? '0' : '1';
    if (this.advancedToggle) {
      this.advancedToggle.textContent = expanded ? 'Hide Advanced Options' : 'View Advanced Options';
      this.advancedToggle.setAttribute('aria-expanded', String(expanded));
    }
  }

  bindEvents() {
    if (!this.container) return;

    // Option Buttons
    this.buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const option = btn.dataset.option;
        const value = btn.dataset.value;
        this.updateState(option, value);

        const currentStep = btn.closest('.rm-step');
        if (currentStep) {
          currentStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Auto-scroll the grid to center the selected button horizontally
        const grid = btn.closest('.rm-options-grid');
        if (grid) {
          const btnRect = btn.getBoundingClientRect();
          const gridRect = grid.getBoundingClientRect();
          const btnCenter = btnRect.left + btnRect.width / 2;
          const gridCenter = gridRect.left + gridRect.width / 2;
          const scrollDiff = btnCenter - gridCenter;

          grid.scrollBy({ left: scrollDiff, behavior: 'smooth' });
        }
      });
    });

    // File Uploads
    this.setupUploadZone('rm-artwork-input', 'rm-preview-images', 'rm-upload-dropzone', 'rm-upload-spinner', 'rm-remove-upload', 'upload_file');
    this.setupUploadZone('rm-left-artwork-input', 'rm-left-preview-images', 'rm-left-upload-dropzone', 'rm-left-upload-spinner', 'rm-left-remove-upload', 'left_shoulder_file');
    this.setupUploadZone('rm-right-artwork-input', 'rm-right-preview-images', 'rm-right-upload-dropzone', 'rm-right-upload-spinner', 'rm-right-remove-upload', 'right_shoulder_file');

    // Upload help ("How to build your own signet ring") and View Dimensions pop-ups. §10S item 3 (2026-09-29) made View
    // Dimensions a dialog; the upload help got the same on 2026-09-29 (§10U round 3): announced as a dialog with its
    // title, the focus moves in, Tab stays inside, Escape closes, the focus returns; both X buttons are named "Close".
    this.wireDialog(this.uploadModalOverlay, this.uploadHelpBtn, [this.uploadModalClose, document.getElementById('rm-upload-modal-done')], 'rm-upload-modal-title');
    // Upload guide (2026-10-01): the "free check" row closes the guide and opens the theme's design-help sheet when the page has one.
    const guideCheck = document.getElementById('rm-upload-guide-check');
    if (guideCheck) guideCheck.addEventListener('click', () => { if (this.uploadModalClose) this.uploadModalClose.click(); const t = document.querySelector('[data-help-open]'); if (t) setTimeout(() => t.click(), 60); });
    this.wireDialog(this.sizeModalOverlay, this.sizeHelpBtn, [this.sizeModalClose, this.sizeModalCloseBtn], 'rm-size-modal-title');

    // Upload Modal Carousel Sync
    const uploadCarouselTrack = document.getElementById('rm-upload-carousel-track');
    const uploadStepNodes = document.querySelectorAll('.rm-modal-step-node');
    const uploadDots = document.querySelectorAll('#rm-upload-carousel-dots .rm-dot');

    if (uploadCarouselTrack && uploadStepNodes.length > 0) {
      const carouselCards = uploadCarouselTrack.querySelectorAll('.rm-carousel-card');
      
      const scrollToCard = (index) => {
        const card = carouselCards[index];
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      };

      const makeInteractable = (el, index) => {
        if (!el) return;
        el.style.cursor = 'pointer';
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          scrollToCard(index);
        });
      };

      uploadStepNodes.forEach((node, i) => {
        makeInteractable(node, i);
        makeInteractable(node.querySelector('.rm-modal-step-circle'), i);
        makeInteractable(node.querySelector('.rm-modal-step-label'), i);
      });

      if (uploadDots.length > 0) {
        uploadDots.forEach((dot, i) => {
          makeInteractable(dot, i);
        });
      }

      carouselCards.forEach((card, i) => {
        makeInteractable(card, i);
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = Array.from(carouselCards).indexOf(entry.target);
            if (index !== -1) {
              // Update steps
              uploadStepNodes.forEach((node, i) => {
                if (i <= index) {
                  node.classList.add('active');
                } else {
                  node.classList.remove('active');
                }
                // Fill lines
                const lineFill = node.nextElementSibling?.querySelector('.rm-modal-step-line-fill');
                if (lineFill) {
                  if (i < index) {
                    lineFill.style.width = '100%';
                  } else {
                    lineFill.style.width = '0%';
                  }
                }
              });

              // Update dots
              if (uploadDots.length > 0) {
                uploadDots.forEach((dot, i) => {
                  dot.classList.toggle('active', i === index);
                });
              }
            }
          }
        });
      }, {
        root: uploadCarouselTrack,
        threshold: 0.6 // Trigger when 60% of the card is visible in the viewport
      });

      carouselCards.forEach(card => observer.observe(card));
    }

    // Ring Size Need Help Toggle
    if (this.needHelpBtn) {
      this.needHelpBtn.addEventListener('click', () => {
        const isExpanded = !this.infoBoxSizer.classList.contains('hidden') &&
          !this.infoBoxSize.classList.contains('hidden');
        if (isExpanded) {
          if (this.infoBoxSizer) { this.infoBoxSizer.classList.add('hidden'); this.infoBoxSizer.classList.remove('active'); }
          if (this.infoBoxLater) { this.infoBoxLater.classList.add('hidden'); this.infoBoxLater.classList.remove('active'); }
          if (this.infoBoxSize) { this.infoBoxSize.classList.add('hidden'); this.infoBoxSize.classList.remove('active'); }
        } else {
          if (this.infoBoxSizer) { this.infoBoxSizer.classList.remove('hidden'); this.infoBoxSizer.classList.add('active'); }
          if (this.infoBoxLater) { this.infoBoxLater.classList.add('hidden'); this.infoBoxLater.classList.remove('active'); }
          if (this.infoBoxSize) { this.infoBoxSize.classList.remove('hidden'); this.infoBoxSize.classList.add('active'); }
        }
      });
    }

    // Ring Size Selection
    let userInteractedWithPicker = false;
    if (this.carouselItems) {
      const markInteracted = () => userInteractedWithPicker = true;
      this.carouselItems.addEventListener('touchstart', markInteracted, { passive: true });
      this.carouselItems.addEventListener('mousedown', markInteracted, { passive: true });
      this.carouselItems.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          this.carouselItems.scrollBy({ left: e.deltaY, behavior: 'auto' });
        }
        markInteracted();
      }, { passive: false });
      this.carouselItems.addEventListener('keydown', markInteracted, { passive: true });
    }

    let scrollTimeout;
    let programmaticScrollTimer;

    this.allSizeOptions.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const btnElement = e.currentTarget;
        const val = btnElement.dataset.value;

        clearTimeout(scrollTimeout);
        // Tapping the chosen size again un-chooses it (Mike 2026-10-27, standing rule for all versions)
        this.state.ringsize = this.state.ringsize === val ? '' : val;
        this._ringsizeTouched = true;
        const isCarouselBtn = btnElement.classList.contains('rm-carousel-size-btn');
        userInteractedWithPicker = isCarouselBtn;
        this.updateCheckmarks();
        this.renderState();

        const currentStep = btnElement.closest('.rm-step');

        if (this.state.ringsize && isCarouselBtn && this.carouselItems) {
          const btnRect = btnElement.getBoundingClientRect();
          const contRect = this.carouselItems.getBoundingClientRect();
          const btnCenter = btnRect.left + btnRect.width / 2;
          const contCenter = contRect.left + contRect.width / 2;
          const scrollDiff = btnCenter - contCenter;
          this.isProgrammaticScroll = true;
          clearTimeout(programmaticScrollTimer);
          programmaticScrollTimer = setTimeout(() => { this.isProgrammaticScroll = false; }, 350);
          this.carouselItems.scrollBy({ left: scrollDiff, behavior: 'smooth' });
        }
      });
    });

    // iOS-style Picker Scroll Listener
    if (this.carouselItems) {
      this.carouselItems.addEventListener('scroll', () => {
        if (this.isProgrammaticScroll || !userInteractedWithPicker) return;

        const containerRect = this.carouselItems.getBoundingClientRect();
        const center = containerRect.left + containerRect.width / 2;

        let closestBtn = null;
        let minDiff = Infinity;

        const sizeBtns = this.carouselItems.querySelectorAll('.rm-carousel-size-btn');
        sizeBtns.forEach(btn => {
          const rect = btn.getBoundingClientRect();
          const btnCenter = rect.left + rect.width / 2;
          const diff = Math.abs(center - btnCenter);
          if (diff < minDiff) {
            minDiff = diff;
            closestBtn = btn;
          }
        });

        // Update UI instantly
        if (closestBtn && !closestBtn.classList.contains('active')) {
          sizeBtns.forEach(opt => {
            if (opt === closestBtn) {
              opt.classList.add('active');
              opt.classList.remove('dimmed');
            } else {
              opt.classList.remove('active');
              opt.classList.add('dimmed');
            }
          });
        }

        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (this.isProgrammaticScroll || !userInteractedWithPicker) return;
          if (closestBtn && this.state.ringsize !== closestBtn.dataset.value) {
            this.state.ringsize = closestBtn.dataset.value;
            this._ringsizeTouched = true;
            
            this.updateCheckmarks();
            this.renderState(); // Update pricing/etc if dependent on size

            if (this.infoBoxSizer) this.infoBoxSizer.classList.add('hidden');
            if (this.infoBoxLater) this.infoBoxLater.classList.add('hidden');
            if (this.infoBoxSize) this.infoBoxSize.classList.remove('hidden');
          }
        }, 100);
      }, { passive: true });
    }

    // Carousel Navigation
    if (this.carouselPrev && this.carouselItems) {
      this.carouselPrev.addEventListener('click', () => {
        userInteractedWithPicker = true;
        this.carouselItems.scrollBy({ left: -150, behavior: 'smooth' });
      });
    }
    if (this.carouselNext && this.carouselItems) {
      this.carouselNext.addEventListener('click', () => {
        userInteractedWithPicker = true;
        this.carouselItems.scrollBy({ left: 150, behavior: 'smooth' });
      });
    }

    // Advanced Options Toggle
    if (this.advancedToggle && this.advancedContainer) {
      // Screen readers hear whether the panel is open (aria-expanded), 2026-09-29.
      this.advancedToggle.setAttribute('aria-expanded', String(this.advancedContainer.classList.contains('expanded')));
      if (this.advancedContainer.id) this.advancedToggle.setAttribute('aria-controls', this.advancedContainer.id);
      this.advancedToggle.addEventListener('click', () => {
        this.setAdvancedExpanded(!this.advancedContainer.classList.contains('expanded'));
      });
    }

    // Inscription
    if (this.inscriptionInput) {
      this.inscriptionInput.addEventListener('input', (e) => {
        if (e.target.value.length > 25) {
          e.target.value = e.target.value.substring(0, 25);
        }
        this.state.inscription = e.target.value;
        const warning = document.getElementById('rm-inscription-warning');
        if (warning) {
          if (e.target.value.length >= 25) {
            warning.classList.remove('hidden');
          } else {
            warning.classList.add('hidden');
          }
        }
        this.renderState();
      });
    }
    if (this.inscriptionDefer) {
      this.inscriptionDefer.addEventListener('click', () => {
        this.state.inscription = 'Deferred';
        this.inscriptionInput.value = 'Deferred';
      });
    }

    // Side Engraving Text Inputs
    const leftText = document.getElementById('rm-left-shoulder-text');
    if (leftText) {
      leftText.addEventListener('input', (e) => {
        if (e.target.value.length > 15) {
          e.target.value = e.target.value.substring(0, 15);
        }
        this.state.left_shoulder_value = e.target.value;
        const warning = document.getElementById('rm-left-text-warning');
        if (warning) {
          if (e.target.value.length >= 15) {
            warning.classList.remove('hidden');
          } else {
            warning.classList.add('hidden');
          }
        }
        this.renderState();
      });
    }
    const rightText = document.getElementById('rm-right-shoulder-text');
    if (rightText) {
      rightText.addEventListener('input', (e) => {
        if (e.target.value.length > 15) {
          e.target.value = e.target.value.substring(0, 15);
        }
        this.state.right_shoulder_value = e.target.value;
        const warning = document.getElementById('rm-right-text-warning');
        if (warning) {
          if (e.target.value.length >= 15) {
            warning.classList.remove('hidden');
          } else {
            warning.classList.add('hidden');
          }
        }
        this.renderState();
      });
    }

    // Add to Cart
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.submitCart());
    }

    // Share, Save & Reset Actions
    if (this.saveBuildBtn) {
      this.saveBuildBtn.addEventListener('click', () => {
        this.copyDesignLink('rm-save-thought', 'Copy this link to come back to your ring anytime:').then((copied) => {
          this.saveBuildBtn.classList.add('active');
          if (!copied) return;
          this.saveBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
          if (this.saveBuildBtn.nextElementSibling) {
            this.saveBuildBtn.nextElementSibling.textContent = 'Copied!';
          }
        });
      });
    }

    if (this.shareBuildBtn) {
      this.shareBuildBtn.addEventListener('click', () => {
        this.copyDesignLink('rm-share-thought', 'Copy this link to share your ring:').then((copied) => {
          this.shareBuildBtn.classList.add('active');
          if (!copied) return;
          this.shareBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
          if (this.shareBuildBtn.nextElementSibling) {
            this.shareBuildBtn.nextElementSibling.textContent = 'Copied!';
          }
        });
      });
    }

    if (this.resetBuildBtn) {
      this.resetBuildBtn.addEventListener('click', () => {
        if (!this.resetBuildBtn.classList.contains('active')) {
          this.resetBuildBtn.classList.add('active');
          this.resetBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>`;
          if (this.resetBuildBtn.nextElementSibling) {
            this.resetBuildBtn.nextElementSibling.textContent = 'Confirm Reset';
          }
          const thoughtBox = document.getElementById('rm-reset-thought');
          if (thoughtBox) thoughtBox.classList.remove('hidden');
          return;
        }

        // Reset state to initial defaults; uploads still running are dropped, and the length notes and a cart error
        // from before disappear with the texts they belonged to (2026-09-29)
        this.state = this.defaultState();
        this._ringsizeTouched = false;
        if (this.uploadEpoch) Object.keys(this.uploadEpoch).forEach(key => { this.uploadEpoch[key]++; });
        ['rm-inscription-warning', 'rm-left-text-warning', 'rm-right-text-warning'].forEach(id => {
          const note = document.getElementById(id);
          if (note) note.classList.add('hidden');
        });
        this.showCartError(false);

        if (this.inscriptionInput) this.inscriptionInput.value = '';
        const leftInput = document.getElementById('rm-left-shoulder-text');
        if (leftInput) leftInput.value = '';
        const rightInput = document.getElementById('rm-right-shoulder-text');
        if (rightInput) rightInput.value = '';

        // Clear file inputs visually
        const dropzones = [
          { input: 'rm-artwork-input', preview: 'rm-preview-images', dropzone: 'rm-upload-dropzone' },
          { input: 'rm-left-artwork-input', preview: 'rm-left-preview-images', dropzone: 'rm-left-upload-dropzone' },
          { input: 'rm-right-artwork-input', preview: 'rm-right-preview-images', dropzone: 'rm-right-upload-dropzone' }
        ];

        dropzones.forEach(dz => {
          const fileInput = document.getElementById(dz.input);
          const preview = document.getElementById(dz.preview);
          const dropzone = document.getElementById(dz.dropzone);
          if (fileInput) fileInput.value = '';
          if (preview) preview.innerHTML = '';
          this.clearUploadError(dz.dropzone);
          if (dropzone) {
            dropzone.classList.remove('has-files');
            const pblock = dropzone.querySelector('.rm-upload-preview');
            if (pblock) pblock.classList.add('hidden');
          }
        });

        // Reset button state
        this.resetBuildBtn.classList.remove('active');
        this.resetBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>`;
        if (this.resetBuildBtn.nextElementSibling) {
          this.resetBuildBtn.nextElementSibling.textContent = 'Reset';
        }
        const thoughtBox = document.getElementById('rm-reset-thought');
        if (thoughtBox) thoughtBox.classList.add('hidden');

        // Scroll to top
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });

        this.renderState();
      });
    }

    // Global click listener to dismiss Share and Reset states
    document.addEventListener('click', (e) => {
      // Ignore events from detached elements (caused by innerHTML replacement during bubbling)
      if (!document.body.contains(e.target)) return;

      // Dismiss Save
      if (this.saveBuildBtn && this.saveBuildBtn.classList.contains('active')) {
        const saveThoughtBox = document.getElementById('rm-save-thought');
        if (!this.saveBuildBtn.contains(e.target) && (!saveThoughtBox || !saveThoughtBox.contains(e.target))) {
          this.saveBuildBtn.classList.remove('active');
          this.saveBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>`;
          if (this.saveBuildBtn.nextElementSibling) {
            this.saveBuildBtn.nextElementSibling.textContent = 'Save';
          }
          if (saveThoughtBox) saveThoughtBox.classList.add('hidden');
        }
      }

      // Dismiss Share
      if (this.shareBuildBtn && this.shareBuildBtn.classList.contains('active')) {
        const shareThoughtBox = document.getElementById('rm-share-thought');
        if (!this.shareBuildBtn.contains(e.target) && (!shareThoughtBox || !shareThoughtBox.contains(e.target))) {
          this.shareBuildBtn.classList.remove('active');
          this.shareBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>`;
          if (this.shareBuildBtn.nextElementSibling) {
            this.shareBuildBtn.nextElementSibling.textContent = 'Share';
          }
          if (shareThoughtBox) shareThoughtBox.classList.add('hidden');
        }
      }

      // Dismiss Reset
      if (this.resetBuildBtn && this.resetBuildBtn.classList.contains('active')) {
        const resetThoughtBox = document.getElementById('rm-reset-thought');
        if (!this.resetBuildBtn.contains(e.target) && (!resetThoughtBox || !resetThoughtBox.contains(e.target))) {
          this.resetBuildBtn.classList.remove('active');
          this.resetBuildBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 20px; height: 20px;"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>`;
          if (this.resetBuildBtn.nextElementSibling) {
            this.resetBuildBtn.nextElementSibling.textContent = 'Reset';
          }
          if (resetThoughtBox) resetThoughtBox.classList.add('hidden');
        }
      }
    });

    // Intercept Dynamic Checkout (Shop Pay)
    const dynamicCheckoutContainer = this.container.querySelector('.rm-dynamic-checkout-container');
    if (dynamicCheckoutContainer) {
      dynamicCheckoutContainer.addEventListener('click', (e) => {
        if (!this.validateCustomizer()) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true); // use capture phase
    }

    // Sticky Cart Observer
    const stickyCartWrapper = this.container.querySelector('.rm-sticky-cart-wrapper');
    if (stickyCartWrapper) {
      const originalParent = stickyCartWrapper.parentNode;
      const originalNextSibling = stickyCartWrapper.nextSibling;

      // Find the closest color scheme to preserve when moving to body
      const colorSchemeElement = stickyCartWrapper.closest('[color-scheme]');
      const colorSchemeValue = colorSchemeElement ? colorSchemeElement.getAttribute('color-scheme') : null;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            stickyCartWrapper.classList.add('is-sticky');
            if (colorSchemeValue) {
              stickyCartWrapper.setAttribute('color-scheme', colorSchemeValue);
            }
            // Move to body to break out of all z-index stacking contexts from parent sections
            document.body.appendChild(stickyCartWrapper);
            observer.disconnect();
          }
        });
      }, {
        root: null,
        threshold: 0.5
      });
      observer.observe(stickyCartWrapper);
    }
  }

  // Update customizer state for a given option key and value
  updateState(option, value) {
    // Check if updating left or right shoulder side engraving type
    if (option === 'left_shoulder' || option === 'right_shoulder') {
      // Check if side engraving type selection has changed
      if (this.state[option + '_type'] !== value) {
        // Set new side engraving input type (Text, Artwork, or Upload)
        this.state[option + '_type'] = value;
        // Reset shoulder text/artwork value to empty string when switching type
        this.state[option + '_value'] = '';
        // Reset shoulder uploaded file to null when switching type (and drop an upload still running for it)
        this.state[option + '_file'] = null;
        if (this.uploadEpoch && this.uploadEpoch[option + '_file'] !== undefined) this.uploadEpoch[option + '_file']++;
        // ...and the upload note that belonged to it
        this.clearUploadError(option === 'left_shoulder' ? 'rm-left-upload-dropzone' : 'rm-right-upload-dropzone');
      }
    } else {
      // Set general customizer state value
      this.state[option] = value;
    }
    // Re-render customizer state UI
    this.renderState();
  }

  lazyLoadArtworks() {
    if (this.artworksLoaded) return;
    this.artworksLoaded = true;

    const side_engraving_URLs_dict = {
      "S1": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_1.jpg?v=1706046900",
      "S2": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_2.jpg?v=1706046900",
      "S3": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_3.jpg?v=1706046899",
      "S4": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_4.jpg?v=1706046900",
      "S5": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_5.jpg?v=1706046899",
      "S6": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_6.jpg?v=1706046899",
      "S7": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_7.jpg?v=1706046899",
      "S8": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_8.jpg?v=1706046899",
      "S9": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_9.jpg?v=1706046899",
      "S10": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_10.jpg?v=1706046899",
      "S11": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_11.jpg?v=1706046900",
      "S12": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_12.jpg?v=1706046900",
      "S13": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_13.jpg?v=1706046899",
      "S14": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_14.jpg?v=1706046900",
      "S15": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_15.jpg?v=1706046900",
      "S16": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_16.jpg?v=1706046899",
      "S17": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_17.jpg?v=1706046899",
      "S18": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_18.jpg?v=1706046900",
      "S19": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_19.jpg?v=1706046899",
      "S20": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_20.jpg?v=1706046900",
      "S21": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_21.jpg?v=1706046900",
      "S22": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_22.jpg?v=1706046899",
      "S23": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_23.jpg?v=1706046900",
      "S24": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_24.jpg?v=1706046899",
      "S25": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_25.jpg?v=1706046899",
      "S26": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_26.jpg?v=1706046899",
      "S27": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_27.jpg?v=1706046899",
      "S28": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_28.jpg?v=1706046899",
      "S29": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_29.jpg?v=1706046900",
      "S30": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_30.jpg?v=1706046900",
      "S31": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_31.jpg?v=1706046899",
      "S32": "https://cdn.shopify.com/s/files/1/0839/9942/4788/files/S_32.jpg?v=1706046899"
    };

    const buildGrid = (side, gridId) => {
      const grid = document.getElementById(gridId);
      if (!grid) return;
      Object.entries(side_engraving_URLs_dict).forEach(([key, url]) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `rm-option-btn rm-artwork-btn`;
        btn.dataset.option = `${side}_artwork`;
        btn.dataset.value = key;
        btn.innerHTML = `
          <div class="rm-thumb-wrapper" style="padding: 0;"><img src="${url}" alt="" loading="lazy" style="border-radius: 8px 8px 0 0;"></div>
          <span class="rm-option-name">${key}</span>
        `;
        btn.addEventListener('click', () => {
          grid.querySelectorAll('.rm-option-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.state[`${side}_value`] = key;
          this.renderState();
          // Auto-scroll the grid to center the selected button horizontally
          const btnRect = btn.getBoundingClientRect();
          const gridRect = grid.getBoundingClientRect();
          const btnCenter = btnRect.left + btnRect.width / 2;
          const gridCenter = gridRect.left + gridRect.width / 2;
          const scrollDiff = btnCenter - gridCenter;

          grid.scrollBy({ left: scrollDiff, behavior: 'smooth' });
          // Auto-scroll vertically removed per user request
        });
        grid.appendChild(btn);
      });
    };

    buildGrid('left_shoulder', 'rm-left-artwork-grid');
    buildGrid('right_shoulder', 'rm-right-artwork-grid');
  }

  // R995 (2026-10-30): the button whose data-value equals the chosen value. Compared as text, never put into a CSS
  // selector: a shoulder text such as Mom "Love" or a backslash made querySelector throw, so renderState() stopped on
  // every keystroke and the price and the Shop Pay form kept the last good variant and texts.
  optionBtnFor(btns, value) {
    const want = String(value);
    return Array.from(btns).find(b => b.dataset.value === want) || null;
  }

  renderState() {
    // Clear any error glow
    this.container.querySelectorAll('.rm-error-glow').forEach(el => el.classList.remove('rm-error-glow'));

    // 1. Update active UI buttons
    ['color', 'design', 'shape', 'size', 'side_engravings', 'left_shoulder', 'right_shoulder', 'inscription_type'].forEach(option => {
      const btns = this.container.querySelectorAll(`.rm-option-btn[data-option="${option}"]`);
      btns.forEach(b => b.classList.remove('active'));
      const activeBtn = this.optionBtnFor(btns, this.state[option] || this.state[option + '_type']);
      if (activeBtn) activeBtn.classList.add('active');
    });

    // 2. Update text summaries

    // 2.1 Update ringsize active state visually
    if (this.allSizeOptions) {
      if (this.infoBoxSizer) { this.infoBoxSizer.classList.add('hidden'); this.infoBoxSizer.classList.remove('active'); }
      if (this.infoBoxLater) { this.infoBoxLater.classList.add('hidden'); this.infoBoxLater.classList.remove('active'); }
      if (this.infoBoxSize) { this.infoBoxSize.classList.add('hidden'); this.infoBoxSize.classList.remove('active'); }

      this.allSizeOptions.forEach(opt => {
        if (opt.dataset.value === this.state.ringsize) {
          opt.classList.add('active');
          opt.classList.remove('dimmed');
          opt.setAttribute('aria-checked', 'true');

          const val = this.state.ringsize;
          if (val === 'Send me a free ring sizer') {
            if (this.infoBoxSizer) { this.infoBoxSizer.classList.remove('hidden'); this.infoBoxSizer.classList.add('active'); }
          } else if (val === "I'll let you know later") {
            if (this.infoBoxLater) { this.infoBoxLater.classList.remove('hidden'); this.infoBoxLater.classList.add('active'); }
          } else if (val.startsWith('US')) {
            if (this.infoBoxSize) { this.infoBoxSize.classList.remove('hidden'); this.infoBoxSize.classList.add('active'); }
          }
        } else {
          opt.classList.remove('active');
          opt.setAttribute('aria-checked', 'false');
          if (this.state.ringsize) {
            opt.classList.add('dimmed');
          } else {
            opt.classList.remove('dimmed');
          }
        }
      });
    }

    document.getElementById('rm-val-color').textContent = this.state.color;
    document.getElementById('rm-val-design').textContent = this.state.design;
    document.getElementById('rm-val-shape').textContent = this.state.shape;

    // Price steps come from the store's own variant prices for the ring as chosen (2026-09-29: side engravings add $50
    // on a Small face but $30 on Medium and $20 on Large, and each size step is smaller with side engravings; the fixed
    // labels said "+$50", "+$70" and "+$130" for every ring).
    const priceSteps = this.updateOptionPrices();
    document.getElementById('rm-val-size').textContent = this.state.size + (priceSteps.size[this.state.size] || '');

    const sizeWarning = document.getElementById('rm-size-warning');
    if (sizeWarning) {
      if (this.state.size === 'Small') {
        sizeWarning.innerHTML = 'Ideal for <b>pinky rings</b> and basic designs. For intricate details, choose Medium or Large.';
        sizeWarning.setAttribute('data-size', 'Small');
        sizeWarning.classList.remove('hidden');
      } else if (this.state.size === 'Medium') {
        sizeWarning.innerHTML = 'Great <b>all-rounder</b>. Perfectly proportioned for most fingers and detailed designs.';
        sizeWarning.setAttribute('data-size', 'Medium');
        sizeWarning.classList.remove('hidden');
      } else if (this.state.size === 'Large') {
        sizeWarning.innerHTML = 'Substantial, bold <b>statement piece</b>. Ideal for larger hands, designed to stand out.';
        sizeWarning.setAttribute('data-size', 'Large');
        sizeWarning.classList.remove('hidden');
      } else {
        sizeWarning.classList.add('hidden');
      }
    }

    document.getElementById('rm-val-sides').textContent = this.state.side_engravings + (this.state.side_engravings === 'Yes!' ? priceSteps.sides : '');

    const valInscription = document.getElementById('rm-val-inscription');
    if (valInscription) {
      valInscription.textContent = this.state.inscription_type === 'Plain' ? 'Plain (empty)' : 'Inscribed';
    }

    // 2.5 Side Engravings Advanced State
    const sideDetails = document.getElementById('rm-side-engraving-details');
    if (this.state.side_engravings === 'Yes!') {
      if (sideDetails) sideDetails.classList.remove('hidden');
      this.lazyLoadArtworks();

      const leftText = document.getElementById('rm-left-shoulder-text-container');
      const leftArtwork = document.getElementById('rm-left-shoulder-artwork-container');
      const leftUpload = document.getElementById('rm-left-shoulder-upload-container');
      if (leftText) leftText.classList.toggle('hidden', this.state.left_shoulder_type !== 'Text');
      if (leftArtwork) leftArtwork.classList.toggle('hidden', this.state.left_shoulder_type !== 'Artwork');
      if (leftUpload) leftUpload.classList.toggle('hidden', this.state.left_shoulder_type !== 'Upload');

      const rightText = document.getElementById('rm-right-shoulder-text-container');
      const rightArtwork = document.getElementById('rm-right-shoulder-artwork-container');
      const rightUpload = document.getElementById('rm-right-shoulder-upload-container');
      if (rightText) rightText.classList.toggle('hidden', this.state.right_shoulder_type !== 'Text');
      if (rightArtwork) rightArtwork.classList.toggle('hidden', this.state.right_shoulder_type !== 'Artwork');
      if (rightUpload) rightUpload.classList.toggle('hidden', this.state.right_shoulder_type !== 'Upload');

      // Update active artwork buttons and text input values from state
      ['left_shoulder_artwork', 'right_shoulder_artwork'].forEach(option => {
        const side = option.replace('_artwork', '');
        const btns = this.container.querySelectorAll(`.rm-option-btn[data-option="${option}"]`);
        btns.forEach(b => b.classList.remove('active'));
        const activeBtn = this.optionBtnFor(btns, this.state[side + '_value']);
        if (activeBtn) {
          activeBtn.classList.add('active');
        }
      });

      const leftInput = document.getElementById('rm-left-shoulder-text');
      if (leftInput && leftInput.value !== this.state.left_shoulder_value && this.state.left_shoulder_type === 'Text') {
        leftInput.value = this.state.left_shoulder_value || '';
      }
      const rightInput = document.getElementById('rm-right-shoulder-text');
      if (rightInput && rightInput.value !== this.state.right_shoulder_value && this.state.right_shoulder_type === 'Text') {
        rightInput.value = this.state.right_shoulder_value || '';
      }
    } else {
      if (sideDetails) sideDetails.classList.add('hidden');
    }

    // 2.6 Inside Inscription State
    const inscriptionDetails = document.getElementById('rm-inscription-details');
    if (this.state.inscription_type === 'Inscribed') {
      if (inscriptionDetails) inscriptionDetails.classList.remove('hidden');

      if (this.inscriptionInput && this.inscriptionInput.value !== this.state.inscription) {
        this.inscriptionInput.value = this.state.inscription || '';
      }
    } else {
      if (inscriptionDetails) inscriptionDetails.classList.add('hidden');
    }

    // 3. Update cascading images dynamically
    const colorBtn = this.container.querySelector('.rm-option-btn[data-option="color"].active');
    const colorKey = colorBtn ? colorBtn.dataset.key : 'silver';

    // Move Popular badge for Ring Face Design based on color
    const designPopularBadge = this.container.querySelector('.rm-options-design .rm-thumb-badge');
    if (designPopularBadge) {
      const targetKey = colorKey === 'silver' ? 'raised_black' : 'raised';
      const targetBtn = this.container.querySelector(`.rm-option-btn[data-option="design"][data-key="${targetKey}"]`);
      if (targetBtn && !targetBtn.contains(designPopularBadge)) {
        const wrapper = targetBtn.querySelector('.rm-thumb-wrapper');
        if (wrapper) {
          targetBtn.insertBefore(designPopularBadge, wrapper);
        }
      }
    }

    // Render Color Thumbnails (Static per option, but needs to be populated)
    const colorBtns = this.container.querySelectorAll('.rm-option-btn[data-option="color"]');
    colorBtns.forEach(btn => {
      const cKey = btn.dataset.key;
      const img = btn.querySelector('img');
      if (img && !img.src.includes(cKey)) {
        img.src = this.getAssetUrl(`color_${cKey}.webp`);
      }
    });

    // Render Design Thumbnails
    const designBtns = this.container.querySelectorAll('.rm-option-btn[data-option="design"]:not(.rm-defer-btn)');
    designBtns.forEach(btn => {
      const designKey = btn.dataset.key;
      const img = btn.querySelector('img');
      if (img) img.src = this.getAssetUrl(`design_${colorKey}_${designKey}.webp`);
    });

    // Render Shape Thumbnails
    const shapeBtns = this.container.querySelectorAll('.rm-option-btn[data-option="shape"]:not(.rm-defer-btn)');
    shapeBtns.forEach(btn => {
      const shapeKey = btn.dataset.key;
      const img = btn.querySelector('img');
      if (img) img.src = this.getAssetUrl(`shape_${colorKey}_${shapeKey}.webp`);
    });

    // Render Sides Thumbnails
    const sideBtns = this.container.querySelectorAll('.rm-option-btn[data-option="side_engravings"]:not(.rm-defer-btn)');
    sideBtns.forEach(btn => {
      const sideKey = btn.dataset.key;
      const img = btn.querySelector('img');
      if (img) img.src = this.getAssetUrl(`sides_${colorKey}_${sideKey}.webp`);
    });

    // Select all non-defer size option buttons within the customizer container
    const sizeBtns = this.container.querySelectorAll('.rm-option-btn[data-option="size"]:not(.rm-defer-btn)');
    // Iterate over each size button to dynamically update its thumbnail image source
    sizeBtns.forEach(btn => {
      // Extract the size key attribute (small, medium, large) from the button dataset
      const sizeKey = btn.dataset.key;
      // Find the image element nested within the current size button
      const img = btn.querySelector('img');
      // Check if the image element exists before setting its source
      if (img) {
        // Construct the dynamic asset URL using the current ring color key and size key
        const newSrc = this.getAssetUrl(`size_${colorKey}_${sizeKey}.webp`);
        // Assign the updated cache-busted asset URL to the image element
        img.src = newSrc;
      }
    });

    // Render Inside Inscription Thumbnails
    const insideBtns = this.container.querySelectorAll('.rm-option-btn[data-option="inscription_type"]:not(.rm-defer-btn)');
    insideBtns.forEach(btn => {
      const insKey = btn.dataset.key;
      const img = btn.querySelector('img');
      if (img) img.src = this.getAssetUrl(`inside_${colorKey}_${insKey}.webp`);
    });

    // Render Shoulder Sub-option Thumbnails
    ['left_shoulder', 'right_shoulder'].forEach(shoulder => {
      const btns = this.container.querySelectorAll(`.rm-option-btn[data-option="${shoulder}"]`);
      btns.forEach(btn => {
        const key = btn.dataset.key;
        const img = btn.querySelector('img');
        if (img) img.src = this.getAssetUrl(`sides_${colorKey}_engraved_${key}.webp`);
      });
    });

    // 4. Update total price calculation
    this.calculatePrice();
    this.updateHiddenFormInputs();

    // 5. Update Checkmarks
    this.updateCheckmarks();

    // 6. Sync state to URL and update Summary Island
    this.syncStateToURL();
    this.updateSummaryIsland();

    // Trigger state logger
    if (this.logger) {
      this.logger.triggerLog();
    }
  }

  updateCheckmarks() {
    const steps = [
      { name: 'color', isValid: () => !!this.state.color },
      { name: 'design', isValid: () => !!this.state.design },
      { name: 'shape', isValid: () => !!this.state.shape },
      { name: 'size', isValid: () => !!this.state.size },
      { name: 'upload', isValid: () => this.state.upload_file && this.state.upload_file.length > 0 },
      { name: 'ringsize', isValid: () => !!this.state.ringsize },
      { name: 'side_engravings', isValid: () => !!this.state.side_engravings },
      { name: 'left_shoulder', isValid: () => this.state.side_engravings !== 'Yes!' || this.shoulderValid('left_shoulder') },
      { name: 'right_shoulder', isValid: () => this.state.side_engravings !== 'Yes!' || this.shoulderValid('right_shoulder') },
      { name: 'inscription_type', isValid: () => this.state.inscription_type !== 'Inscribed' || !!this.inscriptionText() }
    ];

    steps.forEach(step => {
      const stepWrapper = this.container.querySelector(`.rm-step[data-step-name="${step.name}"]`);
      if (stepWrapper) {
        const check = stepWrapper.querySelector('.rm-step-check');
        if (check) {
          if (step.isValid()) {
            check.classList.remove('hidden');
          } else {
            check.classList.add('hidden');
          }
        }
      }
    });
  }

  variantPrice(faceSize, sides) {
    const want = sides === 'Yes!' ? ['Yes!', 'Engraved', 'Yes'] : ['Plain', 'Plain (empty)', 'No!', 'No'];
    const size = faceSize === 'Deferred' ? 'Large' : faceSize;
    const v = (window.RM_VARIANTS || []).find(x => {
      const o = [x.option1, x.option2, x.option3].map(t => (t || '').trim());
      return o.includes(this.state.color) && o.includes(size) && want.some(w => o.includes(w));
    });
    return v ? v.price : null;
  }

  priceStep(cents) {
    return cents > 0 ? ` (+$${Math.round(cents / 100).toLocaleString('en-US')})` : '';
  }

  // Writes the "(+$…)" on the face-size buttons and on "Yes!" for side engravings; returns the steps for the labels.
  updateOptionPrices() {
    const steps = { size: {}, sides: '' };
    const sides = this.state.side_engravings === 'Yes!' ? 'Yes!' : 'Plain';
    const small = this.variantPrice('Small', sides);
    this.container.querySelectorAll('.rm-option-btn[data-option="size"]').forEach(btn => {
      const value = btn.dataset.value;
      const price = this.variantPrice(value, sides);
      if (small === null || price === null) return;
      steps.size[value] = this.priceStep(price - small);
      const name = btn.querySelector('.rm-option-name');
      if (name) name.textContent = (value === 'Deferred' ? 'Defer' : value) + steps.size[value];
    });
    const plain = this.variantPrice(this.state.size, 'Plain');
    const engraved = this.variantPrice(this.state.size, 'Yes!');
    if (plain !== null && engraved !== null) {
      steps.sides = this.priceStep(engraved - plain);
      const yes = this.container.querySelector('.rm-option-btn[data-option="side_engravings"][data-value="Yes!"] .rm-option-name');
      if (yes) yes.textContent = 'Yes!' + steps.sides;
    }
    return steps;
  }

  formatKey(str) {
    if (!str) return '';
    return str.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  getAssetUrl(filename) {
    // We assume the Shopify CDN URL can be constructed if we know the base path,
    // but a safer trick is to let Liquid render a placeholder array or use a known base.
    // However, Shopify Asset URLs have hashes.
    // BEST PRACTICE FOR V3: We upload the images to Theme -> Edit Code -> Assets.
    // This is because the Admin > Files dashboard aggressively appends UUIDs to filenames, 
    // breaking our ability to dynamically predict the image URLs. The Assets folder strictly preserves filenames.
    // We will assume window.RM_FILES_URL holds the base path to the files directory.
    const v = window.RM_ASSET_VERSION ? `?v=${window.RM_ASSET_VERSION}` : '';
    return `${window.RM_FILES_URL || ''}${this.cdnPrefix}${filename}${v}`;
  }

  findMatchingVariant() {
    if (!window.RM_VARIANTS) return null;

    let targetSize = this.state.size;
    let targetEngraving = this.state.side_engravings;

    // Handle "Deferred" logic for pricing tiers
    if (targetSize === 'Deferred') targetSize = 'Large';
    if (targetEngraving === 'Deferred') targetEngraving = 'Plain';

    let possibleEngravings = [targetEngraving];
    if (targetEngraving === 'Plain' || targetEngraving === 'No!' || targetEngraving === 'No' || targetEngraving === 'Plain (empty)') {
      possibleEngravings = ['Plain', 'Plain (empty)', 'No!', 'No'];
    } else if (targetEngraving === 'Yes!' || targetEngraving === 'Engraved' || targetEngraving === 'Yes') {
      possibleEngravings = ['Yes!', 'Engraved', 'Yes'];
    }

    // 1. Strict match on Color + Size + Engraving
    const matches = window.RM_VARIANTS.filter(v => {
      const opts = [v.option1, v.option2, v.option3].map(o => (o || '').trim());
      const hasSize = opts.includes(targetSize);
      const hasEngraving = possibleEngravings.some(eng => opts.includes(eng));
      const hasColor = opts.includes(this.state.color);

      return hasColor && hasSize && hasEngraving;
    });

    if (matches.length > 0) {
      const availableMatch = matches.find(v => v.available);
      if (availableMatch) return availableMatch;
    }

    // 2. Fallback: match Color + Size (any available variant for this Color & Size)
    const colorSizeMatches = window.RM_VARIANTS.filter(v => {
      const opts = [v.option1, v.option2, v.option3].map(o => (o || '').trim());
      const hasSize = opts.includes(targetSize);
      const hasColor = opts.includes(this.state.color);
      return hasColor && hasSize;
    });

    const availableColorSizeMatch = colorSizeMatches.find(v => v.available);
    if (availableColorSizeMatch) return availableColorSizeMatch;

    // 3. Fallback: match Color (any available variant for this Color)
    const colorMatches = window.RM_VARIANTS.filter(v => {
      const opts = [v.option1, v.option2, v.option3].map(o => (o || '').trim());
      return opts.includes(this.state.color);
    });

    const availableColorMatch = colorMatches.find(v => v.available);
    if (availableColorMatch) return availableColorMatch;

    // 4. Ultimate fallback to first match or any available variant
    return (matches && matches[0]) || window.RM_VARIANTS.find(v => v.available) || window.RM_VARIANTS[0];
  }

  calculatePrice() {
    const variant = this.findMatchingVariant();
    if (variant && this.priceDisplay) {
      const priceStr = (variant.price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      this.priceDisplay.textContent = ` \u00A0-\u00A0${priceStr}`;

      // Dynamically update the Shopify Installments widget so prices re-calculate
      const installmentsEl = document.querySelector('shopify-payment-terms');
      if (installmentsEl) installmentsEl.setAttribute('variant-id', variant.id);

      const installmentsInput = document.querySelector('.rm-installments-wrapper input[name="id"]');
      if (installmentsInput) installmentsInput.value = variant.id;
    }
  }

  updateHiddenFormInputs() {
    const form = document.getElementById('ringmaker-product-form');
    if (!form) return;

    // We must find the correct variant
    const variant = this.findMatchingVariant();
    if (variant) {
      // Set the main ID input
      let idInput = form.querySelector('input[name="id"]');
      if (!idInput) {
        idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        form.appendChild(idInput);
      }
      idInput.value = variant.id;
    }

    // Clear old properties
    form.querySelectorAll('input[name^="properties["]').forEach(el => el.remove());

    Object.entries(this.cartProperties()).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      form.appendChild(input);
    });
  }

  // One list of line-item properties for both ways a ring reaches the cart (the Add to Cart request and the form that
  // Shop Pay / Buy Now submits), so the two can never differ again (2026-09-29: the inscription fix had to be made in
  // both places). Only uploaded files (CDN URL strings) are written; a local File object never becomes a
  // '[Pending Upload]' placeholder (R6630): submitCart uploads it first or stops.
  cartProperties() {
    const props = {
      'Color': this.state.color,
      'Ring Face Design': this.state.design,
      'Ring Face Shape': this.state.shape,
      'Ring Face Size': this.state.size,
      'Ring Size': this.state.ringsize,
      'Side Engravings': this.state.side_engravings
    };

    const addFile = (file, nameProp, fileProp) => {
      if (typeof file !== 'string' || !file) return;
      const url = (file.startsWith('http') || file.startsWith('//')) ? file : this.getAssetUrl(file);
      const name = url.split('/').pop().split('?')[0];
      if (nameProp) props[nameProp] = `[Upload] ${name}`;
      props[fileProp] = url;
    };

    if (this.state.side_engravings === 'Yes!') {
      [['left_shoulder', 'Left Shoulder'], ['right_shoulder', 'Right Shoulder']].forEach(([side, label]) => {
        const type = this.state[side + '_type'];
        const value = String(this.state[side + '_value'] || '').trim();
        const files = this.state[side + '_file'];
        if ((type === 'Text' || type === 'Artwork') && value) {
          props[label] = `[${type}] "${value}"`;
        } else if (type === 'Upload' && files && files.length > 0) {
          files.forEach((file, index) => {
            addFile(file, index === 0 ? label : `${label} ${index + 1}`, index === 0 ? `${label} File` : `${label} File ${index + 1}`);
          });
        }
      });
    }

    const inscription = this.inscriptionText();
    if (inscription) props['Inside Inscription'] = inscription;

    if (this.state.upload_file && this.state.upload_file.length > 0) {
      this.state.upload_file.forEach((file, index) => {
        addFile(file, null, index === 0 ? 'Artwork File' : `Artwork File ${index + 1}`);
      });
    }
    return props;
  }

  // Validate customizer selections before adding to cart or launching checkout
  validateCustomizer() {
    // Initialize error message variable as null
    let errorMsg = null;
    // Initialize error target variable for the single section element to highlight
    let errorTarget = null;
    // Track whether the error belongs to an active advanced option step
    let isAdvancedError = false;

    // Check if main artwork file is uploaded
    if (!this.state.upload_file || this.state.upload_file.length === 0) {
      // Set error message for missing artwork upload
      errorMsg = "Please upload your artwork to continue.";
      // Target the upload step container element
      errorTarget = this.container.querySelector('[data-step-name="upload"]');
    // Check if ring size has been selected
    } else if (!this.state.ringsize) {
      // Set error message for missing ring size
      errorMsg = "Please select a Ring Size or choose an alternative option.";
      // Target the ring size step container element
      errorTarget = this.container.querySelector('[data-step-name="ringsize"]');
    // Check if side engravings section is enabled with Yes!
    } else if (this.state.side_engravings === 'Yes!') {
      // Validate left shoulder input based on selected input type
      const leftValid = this.shoulderValid('left_shoulder');
      // Validate right shoulder input based on selected input type
      const rightValid = this.shoulderValid('right_shoulder');

      // If left shoulder is incomplete set error message and target left shoulder step
      if (!leftValid) {
        // Set error message for left side engraving
        errorMsg = "Please provide your Left Side engraving details.";
        // Target specifically the left shoulder step element
        errorTarget = this.container.querySelector('[data-step-name="left_shoulder"]');
        // Mark as advanced option error
        isAdvancedError = true;
      // If right shoulder is incomplete set error message and target right shoulder step
      } else if (!rightValid) {
        // Set error message for right side engraving
        errorMsg = "Please provide your Right Side engraving details.";
        // Target specifically the right shoulder step element
        errorTarget = this.container.querySelector('[data-step-name="right_shoulder"]');
        // Mark as advanced option error
        isAdvancedError = true;
      }
    }
    
    // Check inside inscription if set to inscribed and text is missing
    if (!errorMsg && this.state.inscription_type === 'Inscribed' && (!this.state.inscription || !this.state.inscription.trim())) {
      // Set error message for inside inscription
      errorMsg = "Please provide your Inside Inscription details.";
      // Target specifically the inscription step container element
      errorTarget = this.container.querySelector('[data-step-name="inscription_type"]');
      // Mark as advanced option error
      isAdvancedError = true;
    }

    // Clear previous error highlight classes from customizer container
    this.container.querySelectorAll('.rm-error-glow').forEach(el => el.classList.remove('rm-error-glow'));

    // Check if any error was detected during validation
    if (errorMsg) {
      // Check if error target element was identified
      if (errorTarget) {
        // Automatically expand advanced options container ONLY if the error belongs to an advanced step
        if (isAdvancedError && this.advancedContainer && !this.advancedContainer.classList.contains('expanded')) {
          this.setAdvancedExpanded(true);
        }
        // Add single red error highlight glow to target step element
        errorTarget.classList.add('rm-error-glow');
        // Smoothly scroll target element into view centered
        errorTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Screen-reader users hear what is missing (sighted shoppers see the glow and the scroll)
      this.announce(errorMsg);
      // Return false indicating validation failed
      return false;
    }

    // Return true indicating customizer validation succeeded
    return true;
  }

  // First-party event log (2026-09-29, CUSTOM_THEME §10U A): whether Add to Cart worked reaches the Render backend.
  logEvent(name, data) {
    try { if (this.logger && typeof this.logger.track === 'function') this.logger.track(name, data || {}); } catch (e) {}
  }

  async submitCart(isShopPay = false) {
    // One submission at a time (a double tap used to add the ring twice). v4.4 (Mike 2026-10-17): after a ring goes in,
    // taps stay ignored for a short guard (ATC_GUARD_MS from the tap, at least ATC_AFTER_MS after the add), then a new tap
    // adds another ring as usual. A refused or failed add frees the button at once.
    if (this.submitting) return;
    this.submitting = true;
    const started = Date.now();
    let added = false;
    try {
      // A photo that is still being resized or uploaded is waited for instead of being reported as missing.
      if (this.pendingUploads && this.pendingUploads.size) {
        const label = this.addToCartBtn ? this.addToCartBtn.querySelector('.hdt-btn-atc_text, .rm-btn-text') : null;
        const before = label ? label.textContent : null;
        if (label) label.textContent = 'Uploading…';
        if (this.addToCartBtn) this.addToCartBtn.classList.add('loading');
        await this.waitForPendingUploads();
        if (this.addToCartBtn) this.addToCartBtn.classList.remove('loading');
        if (label && before !== null) label.textContent = before;
      }
      added = (await this.submitCartNow(isShopPay)) === true;
      return added;
    } finally {
      if (added) {
        const ATC_GUARD_MS = 900, ATC_AFTER_MS = 250;
        const wait = Math.max(ATC_GUARD_MS - (Date.now() - started), ATC_AFTER_MS);
        await new Promise(r => setTimeout(r, wait));
      }
      this.setAdding(false, added);
      this.submitting = false;
    }
  }

  // "Adding to cart" with a calm pulse while a ring goes in (product.css .rs-adding; no pulse with reduced motion).
  setAdding(on, added = false) {
    const btn = this.addToCartBtn;
    if (!btn) return;
    const text = btn.querySelector('.hdt-btn-atc_text') || btn.querySelector('.rm-btn-text');
    const say = t => { if (text) text.textContent = t; else btn.innerHTML = '<span class="rm-btn-text">' + t + '</span>'; };
    clearTimeout(this.addedTimer);
    if (on) {
      btn.classList.add('loading', 'rs-adding');
      btn.setAttribute('aria-busy', 'true');
      say('Adding to cart');
      this.announce('Adding to cart');
      return;
    }
    if (!btn.classList.contains('rs-adding')) return;
    btn.classList.remove('loading', 'rs-adding');
    btn.removeAttribute('aria-busy');
    if (added) {
      say('Added!');
      this.addedTimer = setTimeout(() => { if (!this.submitting) say('Add to Cart'); }, 1500);
    } else {
      say('Add to Cart');
    }
  }

  async submitCartNow(isShopPay = false) {
    this.showCartError(false);
    try {
      if (!this.validateCustomizer()) {
        const missing = this.container.querySelector('.rm-error-glow[data-step-name]');
        this.logEvent('atc_blocked', { reason: 'missing_step', step: missing ? missing.getAttribute('data-step-name') : null });
        return;
      }

      const variant = this.findMatchingVariant();
      if (!variant) {
        this.logEvent('atc_failed', { reason: 'no_variant' });
        return;
      }

      // Re-attempt any artwork still held as a local File object; if it cannot reach the CDN, keep the cart closed
      // and say so calmly in that upload box ('[Pending Upload]' placeholder prevention, R6630).
      const failedKey = await this.ensureUploadsCompleted();
      if (failedKey) {
        this.logEvent('atc_blocked', { reason: 'upload_failed', step: String(failedKey).slice(0, 40) });
        const zone = this.uploadZoneFor(failedKey);
        if (zone) {
          this.showUploadError(zone.dropzone, this.uploadErrorMessage(null, '', false));
          const step = this.container.querySelector(`[data-step-name="${zone.step}"]`);
          if (step) {
            step.classList.add('rm-error-glow');
            step.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      // Build JSON payload (the same properties as the Buy Now form)
      const properties = this.cartProperties();

      const payload = {
        id: variant.id,
        quantity: 1,
        properties: properties
      };

      this.setAdding(true);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let result = {};
      try { result = await response.json(); } catch (e) { result = {}; }

      if (!response.ok) {
        throw new Error(result.description || result.message || `Error adding to cart (HTTP ${response.status})`);
      }

      this.logEvent('atc_success', { via: isShopPay ? 'buy_now' : 'button', variant_id: result.variant_id || variant.id, price: result.final_line_price != null ? result.final_line_price : result.price });

      if (isShopPay) {
        this.logEvent('checkout_click', { source: 'designer_buy_now' });
        window.location.href = '/checkout';
      } else {
        // Trigger theme cart drawer properly
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));

        // Update header cart count
        fetch(window.Shopify.routes.root + 'cart.js')
          .then(res => res.json())
          .then(cart => {
            document.querySelectorAll('hdt-cart-count').forEach(el => el.textContent = cart.item_count);
          });
        
        // Directly trigger HDT theme's drawer open if possible
        const cartDrawer = document.querySelector('hdt-cart-drawer');
        if (cartDrawer) {
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
          else cartDrawer.setAttribute('open', '');
          
          // Re-render HDT cart
          fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`)
            .then(res => res.text())
            .then(text => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const cartItems = html.querySelector('.hdt-mini-cart__wrap');
              if (cartItems && document.querySelector('.hdt-mini-cart__wrap')) {
                document.querySelector('.hdt-mini-cart__wrap').innerHTML = cartItems.innerHTML;
              }
              const cartCount = html.querySelector('hdt-cart-drawer');
              if (cartCount && cartCount.hasAttribute('data-count')) {
                cartDrawer.setAttribute('data-count', cartCount.getAttribute('data-count'));
              }
            });
        }
        
        // The button shows "Added!" once the short guard in submitCart is over (setAdding).
      }
      return true;
    } catch (error) {
      this.logEvent('atc_failed', { reason: String((error && error.message) || 'error').slice(0, 160) });
      console.error('Error adding to cart:', error);
      this.showCartError(true);
      // the button comes back at once (submitCart's finally calls setAdding(false))
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.RM_Customizer = new RingmakerCustomizer();
});

